import { useRef, useEffect } from 'react';

const isRefObject = <T>(
  ref: React.Ref<T> | undefined,
): ref is React.RefObject<T> =>
  !!ref && typeof ref === 'object' && 'current' in ref;

/**
 * A small hook to *use* a ref passed in as a prop.
 *
 * Behavior:
 * - If the parent passed an **object ref** (`useRef()` result), that same object is returned.
 * - If the parent passed a **callback ref**, this hook keeps an internal ref and invokes the
 *   parent's callback on mount/unmount to keep things in sync.
 *
 * @template T - The element or instance type the ref points to.
 * @param {React.Ref<T>} [ref] - A ref passed in as a prop (object or callback).
 * @returns {React.RefObject<T | null>} A ref object safe to use within the component.
 *
 * @example
 * function MyComponent({ ref }: { ref?: React.Ref<HTMLDivElement> }) {
 *   const safeRef = useForwardedRef(ref);
 *
 *   useEffect(() => {
 *     if (safeRef.current) {
 *       // Do something with the element
 *     }
 *   }, []);
 *
 *   return <div ref={safeRef} />;
 * }
 *
 * @see https://react.dev/reference/react/useRef
 * @see https://react.dev/learn/referencing-values-with-refs
 */
const useForwardedRef = <T>(ref?: React.Ref<T>): React.RefObject<T | null> => {
  const innerRef = useRef<T | null>(null);

  useEffect(() => {
    if (typeof ref !== 'function') {
      return () => null;
    }

    ref(innerRef.current);

    return () => {
      ref(null);
    };
  }, [ref]);

  return isRefObject(ref) ? ref : innerRef;
};

export { useForwardedRef };
